// ABOUTME: Custom navigation sidebar component replacing Flowbite Sidebar
// ABOUTME: Supports collapsed state, items, item groups with consistent API

import type { FC, ReactNode, HTMLAttributes } from "react"
import React, { createContext, useContext } from "react"
import { cn } from "@shared/lib/utils"

// Context for collapsed state
const SidebarCollapseContext = createContext<boolean>(false)

// Main Sidebar component
export interface CNavSidebarProps extends HTMLAttributes<HTMLElement> {
  children: ReactNode
  collapsed?: boolean
  collapseBehavior?: "collapse" | "hide"
}

type CNavSidebarComponent = FC<CNavSidebarProps> & {
  Items: typeof SidebarItems
  ItemGroup: typeof SidebarItemGroup
  Item: typeof SidebarItem
}

const CNavSidebarBase: FC<CNavSidebarProps> = ({
  children,
  collapsed = false,
  collapseBehavior = "collapse",
  className,
  ...props
}) => {
  if (collapseBehavior === "hide" && collapsed) {
    return null
  }

  return (
    <SidebarCollapseContext.Provider value={collapsed}>
      <aside
        className={cn("h-full", collapsed ? "w-16" : "w-64", "transition-all duration-300", className)}
        {...props}
      >
        <div className="h-full overflow-y-auto overflow-x-hidden">{children}</div>
      </aside>
    </SidebarCollapseContext.Provider>
  )
}

// Sidebar Items container
interface SidebarItemsProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode
}

const SidebarItems: FC<SidebarItemsProps> = ({ children, className, ...props }) => {
  return (
    <div
      className={cn("flex flex-col", className)}
      {...props}
    >
      {children}
    </div>
  )
}

// Sidebar Item Group
interface SidebarItemGroupProps extends HTMLAttributes<HTMLUListElement> {
  children: ReactNode
}

const SidebarItemGroup: FC<SidebarItemGroupProps> = ({ children, className, ...props }) => {
  return (
    <ul
      className={cn("space-y-2", className)}
      {...props}
    >
      {children}
    </ul>
  )
}

// Sidebar Item
interface SidebarItemProps extends HTMLAttributes<HTMLLIElement> {
  children?: ReactNode
  /** A component, a render function, or a ready element — the last needs no component type per render. */
  icon?: FC<{ className?: string }> | (() => ReactNode) | ReactNode
  as?: React.ElementType
  href?: string
}

const SidebarItem: FC<SidebarItemProps> = ({
  children,
  icon: Icon,
  as: Component = "a",
  className,
  href,
  ...props
}) => {
  const collapsed = useContext(SidebarCollapseContext)

  const content = (
    <>
      {Icon && <div className="flex-shrink-0">{typeof Icon === "function" ? <Icon /> : Icon}</div>}
      {!collapsed && children && <span className="flex-1 whitespace-nowrap">{children}</span>}
    </>
  )

  return (
    <li {...props}>
      {Component === "a" ? (
        <a
          href={href}
          className={cn("flex items-center p-2 rounded-lg transition-colors", className)}
        >
          {content}
        </a>
      ) : (
        <div className={cn("flex items-center p-2 rounded-lg transition-colors", className)}>{content}</div>
      )}
    </li>
  )
}

export const CNavSidebar = CNavSidebarBase as CNavSidebarComponent
CNavSidebar.Items = SidebarItems
CNavSidebar.ItemGroup = SidebarItemGroup
CNavSidebar.Item = SidebarItem
